import { ChargeEvent } from "@fairfleet/geotab";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { uniq } from "lodash";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { RESULTS_LIMIT_CHARGE_EVENT } from "@/constants/resultsLimits";

const LOOKBACK_MS = 1000 * 60 * 60 * 24 * 7;

/**
 * Fetches the charging events for the given device IDs.
 *
 * @param devices - The device ids.
 * @returns - The array of charging events.
 */

export function useChargeEventsByDeviceIds(devices?: string[]) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  devices = devices ? uniq(devices) : devices;
  return useQuery({
    queryKey: ["chargingEvents", activeSessionId, devices],

    queryFn: async ({ signal }): Promise<{ [deviceId: string]: ChargeEvent[] }> => {
      if (!devices) {
        return Promise.resolve({});
      }

      const chargeEventPromises = devices.map(async id => {
        const chargeEvents = await geotabClient.get(
          "ChargeEvent",
          {
            deviceSearch: { id },
            fromDate: new Date(Date.now() - LOOKBACK_MS),
          },
          RESULTS_LIMIT_CHARGE_EVENT,
          signal,
        );
        return { id, chargeEvents };
      });

      const chargeEventsByDevice = await Promise.all(chargeEventPromises);

      return chargeEventsByDevice.reduce(
        (acc, { id, chargeEvents }) => {
          acc[id] = chargeEvents;
          return acc;
        },
        {} as { [deviceId: string]: ChargeEvent[] },
      );
    },

    placeholderData: keepPreviousData,
  });
}
